import * as React from 'react';
import { motion } from 'framer-motion';
import {
  FaClock,
  FaPhone,
  FaEnvelope,
  FaInstagram,
  FaFacebook,
  FaWhatsapp,
  FaMapMarkerAlt,
} from 'react-icons/fa'; // Import icons from react-icons

// Social Link Component
interface SocialLinkProps {
  href: string;
  platform: string;
  IconComponent: React.ElementType; // Use React.ElementType for icon components
}
const SocialLink: React.FC<SocialLinkProps> = ({ href, platform, IconComponent }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={platform}
    className="text-white hover:text-gray-300 transition duration-300"
    whileHover={{ scale: 1.1, color: '#ffffff' }}
    transition={{ type: 'spring', stiffness: 300 }}
  >
    <IconComponent className="text-2xl w-6 h-6 hover:scale-125 transition-all duration-300 ease-in-out" />
  </motion.a>
);

// Contact Item Component
interface ContactItemProps {
  IconComponent: React.ElementType; // Use React.ElementType for icon components
  label: string;
  children: React.ReactNode;
}
const ContactItem: React.FC<ContactItemProps> = ({ IconComponent, label, children }) => (
  <motion.div
    className="flex items-start gap-4"
    variants={itemVariants}
  >
    <IconComponent className="text-2xl text-white w-6 h-6" />
    <div className="text-sm">
      <p className="font-bold text-white mb-1">{label}</p>
      {children}
    </div>
  </motion.div>
);

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Footer: React.FC = () => {

  return (
    <motion.footer
      id="contactos"
      className="bg-black text-white pt-16 lg:pt-20 pb-6"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-16">
          {/* Column 1: Logo and Social Media */}
          <motion.div
            className="flex flex-col items-center text-center lg:items-start lg:text-left"
            variants={itemVariants}
          >
            <div className="mb-6">
              <img src="/logo.svg" alt="Schique Salon Logo" className="w-52" />
              {/* <span className="font-serif text-5xl italic text-white">Schique</span> */}
            </div>
            <motion.div
              className="flex gap-4 mb-6 w-52 lg:w-full justify-center items-center"
              variants={sectionVariants}
            >
              <SocialLink href="https://www.instagram.com/schiquebeautylounge/" platform="Instagram" IconComponent={FaInstagram} />
              <SocialLink href="https://www.facebook.com/p/SChique-Cabeleireiro-e-Est%C3%A9tica-100064228500528" platform="Facebook" IconComponent={FaFacebook} />
              <SocialLink href="https://wa.me/351919167389" platform="WhatsApp" IconComponent={FaWhatsapp} />
            </motion.div>
          </motion.div>

          {/* Column 2: Contact Information */}
          <motion.div
            className="flex flex-col gap-5 lg:pl-10 "
            variants={sectionVariants}
          >
            <ContactItem IconComponent={FaClock} label="Horário">
              <p>De terça a sábado das 10:30 às 19h</p>
            </ContactItem>
            <a
              href="https://maps.app.goo.gl/16z1R94CNFtHpWzA6"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition duration-300"
            >
              <ContactItem IconComponent={FaMapMarkerAlt} label="Endereço">

                <p className="hover:text-primary transition duration-300">Rua Tito Moreira Rato, Loja 10A Oeiras 2770-224</p>
              </ContactItem>
            </a>
            <ContactItem IconComponent={FaPhone} label="Telefone">
              <a href="tel:214406827" className="hover:text-primary transition duration-300">
                <p className="hover:text-primary transition duration-300">214 406 827</p>
              </a>
              <a href="tel:+351919167389" className="hover:text-primary transition duration-300">
                <p className="hover:text-primary transition duration-300">+351 919 167 389</p>
              </a>
            </ContactItem>
            <a href="mailto:schique@gmail.com" className="hover:text-primary transition duration-300">
              <ContactItem IconComponent={FaEnvelope} label="Email">
                <p className="hover:text-primary transition duration-300">
                  schique@gmail.com
                </p>
              </ContactItem>
            </a>
          </motion.div>
        </div>

        {/* Optional: Copyright line */}
        <motion.div
          className="text-center mt-12 pt-6 border-t border-gray-700 text-xs text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <p>&copy; {new Date().getFullYear()} Schique Salon. All rights reserved.</p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer; 